<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.quote_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                @click="openAddQuote">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataTables"
            :headers="quoteHeaders"
            :server="server"
            use-selection
            store-name="quoteDataTable"
            store-key="Quote"
            :use-archive="useQuoteArchive"
            :use-restore="useQuoteRestore" />
        <hts-modal
            :show="showPopupDeleteQuote"
            :title="$t('labels.quoteDelete')"
            type="confirm"
            :content="$t('labels.confirmDeleteQuote')"
            @hide="showPopupDeleteQuote = false"
            @confirm="handleDeleteQuote" />
        <create-quote
            ref="templateCreateQuote"
            @refreshTables="refreshTables" />
        <add-order
            v-if="checkPermission(layoutPermissions.order_add)"
            ref="addOrder"
            create-form="quote"
            @refreshTables="refreshTables" />
        <router-view @refreshTables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddOrder from '../order/ignore/add-order.vue'
import CreateQuote from './-components/create.vue'
import { checkPermission } from '@/utils/permission-check'
import Status from '~/components/status.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'

import { $urlClient } from '#imports'
import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'
const { $formatters } = useNuxtApp()

const templateCreateQuote = ref()
const openAddQuote = () => {
  templateCreateQuote.value && templateCreateQuote.value.open()
}

const handleViewQuote = (quote: any) => {
  useRouter().push({ name: 'c-id-quote-management-idquote-view', params: { idquote: quote._id } })
}

const addOrder = ref()
const showAddOrder = (item: { [key: string]: any }) => {
  if (addOrder.value) {
    addOrder.value.open()
    addOrder.value.setQuoteData(item)
  }
}

const handleEditQuote = (quote: any) => {
  useRouter().push({ name: 'c-id-quote-management-idquote-edit', params: { idquote: quote._id } })
}

const quoteIdDelete = ref('')
const showPopupDeleteQuote = ref(false)
const handleOpenDeleteQuote = (quoteData: { [key: string]: any }) => {
  quoteIdDelete.value = quoteData._id
  showPopupDeleteQuote.value = true
}

const dataTables = ref()
const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const handleDeleteQuote = () => {
  const api = $urlClient.quote.delete
  $api(api, { body: { ids: [quoteIdDelete.value] } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (responseData.success) {
        process.client && $toast().success($t('labels.deleteQuoteRequestSuccess'))
      }
      refreshTables()
      showPopupDeleteQuote.value = false
    })
    .catch(() => {
      showPopupDeleteQuote.value = false
    })
}

const server = $urlClient.quote.get

const notCancel = (data: any) => {
  // return (data.markupAvailable && ![0, 2].includes(data.statusApprovalMarkup)) || false
  const status = data.status

  if (typeof status !== 'number') {
    return false
  }

  if ([3].includes(status)) {
    return false
  }

  return true
}

const quoteHeaders = [
  {
    field: 'code',
    headerName: $t('labels.code'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'quoteRequestCode',
    headerName: $t('labels.quoteRequestCode'),
    filter: 'agTextColumnFilter',
    sortable: true
  },

  {
    field: 'statusApprovalMarkup',
    headerName: $t('labels.markupApprovalMarkup'),
    valueFormatter: (params: any) => {
      const statusConfirmMarkup = params?.data?.statusApprovalMarkup
      switch (statusConfirmMarkup) {
      case 0:
        return $t('labels.pendingApproval')
      case 1:
        return $t('labels.approved')
      case 2:
        return $t('labels.reject')
      default:
        return ''
      }
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.pendingApproval'), value: 0 },
      { name: $t('labels.approved'), value: 1 },
      { name: $t('labels.reject'), value: 2 }
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      if (params.data?.positions && Array.isArray(params.data?.positions)) {
        const pluckData = $lodash.map(params.data?.positions, 'fullNameNode')

        return $lodash.join(pluckData, ' >> ')
      }
    }
  },
  {
    field: 'customerName',
    headerName: $t('labels.customer'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'commodityName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.commodity'),
    sortable: true
  },
  {
    field: 'workflowName',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.workflow'),
    sortable: true
  },

  {
    field: 'typeContTypeVehicle',
    headerName: 'Loại cont/loại xe',
    valueGetter: (params: any) => {
      const { transportation, contCategory, vehicleCategoryName } = params?.data || {}
      if (transportation === 'CONTAINER' && contCategory) {
        if (params.data?.contCategory === 20) {
          return $t('labels.20f')
        }
        if (params.data?.contCategory === 40) {
          return $t('labels.40f')
        }
        if (params.data?.contCategory === 45) {
          return $t('labels.45f')
        }
      }

      if (transportation !== 'CONTAINER' && vehicleCategoryName) {
        return vehicleCategoryName
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: 'Yêu cầu đặc biệt',
    valueGetter: (params: any) => {
      const results = [] as string[]
      if (params?.data?.mergeContainer) {
        results.push($t('labels.mergeContainer'))
      }

      if (params?.data?.headContainer) {
        results.push($t('labels.headContainer'))
      }

      return results.join(', ')
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'quote',
      label: '',
      keyStatus: 'status'
    },
    minWidth: 230,
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.init'), value: 0, class: { '!bg-yellow-100': true, '!text-yellow-800': true } }, // Init: Màu vàng
      { name: $t('labels.send'), value: 1, class: { '!bg-blue-100': true, '!text-blue-800': true } }, // Send: Màu xanh dương
      { name: $t('labels.orderCreated'), value: 2, class: { '!bg-green-100': true, '!text-green-800': true } }, // Order Created: Màu xanh lá cây
      { name: $t('labels.cancel'), value: 3, class: { '!bg-red-100': true, '!text-red-800': true } } // Cancel: Màu đỏ
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'createdAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    filter: 'agDateColumnFilter',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      storeKey: 'quoteDataTable',
      storeName: 'Quote',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.quote_management),
          click: handleViewQuote,
          isAll: true
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.editQuote'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.quote_edit),
          click: handleEditQuote
        },
        {
          icon: 'hi-plus-circle-icon',
          label: $t('labels.orderAdd'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.order_add),
          click: showAddOrder,
          validate: notCancel
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.quote_delete),
          click: handleOpenDeleteQuote,
          isArchive: true
        }
      ]
    }
  }
]

const useQuoteArchive = computed(() => {
  return { api: $urlClient.quote.archive, permissions: checkPermission(layoutPermissions.quote_archive) }
})
const useQuoteRestore = computed(() => {
  return { api: $urlClient.quote.restore, permissions: checkPermission(layoutPermissions.quote_restore) }
})
</script>
